import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";

const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** Πίνακας ελέγχου αποστολής προγράμματος: ρυθμίσεις, άμεση αποστολή, ιστορικό. */
export default function ScheduleEmailPanel({ isAdmin = false }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("el") ? "el-GR" : "en-GB";
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [openLog, setOpenLog] = useState(null);

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([
      axios.get(`${config.API_BASE_URL}/company-settings`),
      axios.get(`${config.API_BASE_URL}/schedules/email/log`).catch(() => ({ data: [] })),
    ]);
    setSettings(s.data.settings);
    setLog(l.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadPreview = useCallback(async (target) => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/schedules/email/preview`, { params: { target } });
      setPreview(data);
    } catch { setPreview(null); }
  }, []);
  useEffect(() => { if (settings) loadPreview(settings.schedule_email_target); }, [settings, loadPreview]);

  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    await axios.put(`${config.API_BASE_URL}/company-settings`, patch);
    if (patch.schedule_email_target) loadPreview(patch.schedule_email_target);
  };

  const sendNow = async () => {
    if (!window.confirm(t("Send the schedule by email now?"))) return;
    setBusy(true); setFlash(null);
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/schedules/email/send`,
        { target: settings.schedule_email_target });
      setFlash({ type: "ok", text: `${t("Sent to")} ${data.recipients} ${t("people")}` +
        (data.skipped ? ` · ${data.skipped} ${t("without email")}` : "") });
      load();
    } catch {
      setFlash({ type: "err", text: t("Something went wrong — please try again.") });
    } finally { setBusy(false); }
  };

  if (!settings) return null;

  const on = !!settings.schedule_email_enabled;
  const S = {
    card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 18,
            margin: "16px 0", boxShadow: "0 1px 3px rgba(0,0,0,.04)" },
    row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", margin: "10px 0" },
    sel: { border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 10px", fontSize: 14 },
    btn: { border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 14, fontWeight: 600,
           cursor: "pointer", background: "#d97706", color: "#fff" },
    ghost: { border: "1px solid #d1d5db", background: "#fff", borderRadius: 10, padding: "8px 14px",
             fontSize: 13, cursor: "pointer" },
    hint: { color: "#6b7280", fontSize: 13 },
    chip: { background: "#f3f4f6", borderRadius: 999, padding: "2px 10px", fontSize: 12 },
  };

  return (
    <div style={S.card}>
      <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>✉️ {t("Schedule by email")}</h3>
      <p style={S.hint}>
        {t("Each employee receives only their own shifts. Staff without an email address are skipped.")}
      </p>

      {/* Αυτόματη αποστολή */}
      <div style={S.row}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
          <input type="checkbox" checked={on} disabled={!isAdmin}
            onChange={e => save({ schedule_email_enabled: e.target.checked ? 1 : 0 })} />
          {t("Send automatically")}
        </label>
        {on && (
          <>
            <span style={S.hint}>{t("every")}</span>
            <select style={S.sel} value={settings.schedule_email_dow} disabled={!isAdmin}
              onChange={e => save({ schedule_email_dow: Number(e.target.value) })}>
              {DOW.map((d, i) => <option key={d} value={i}>{t(d)}</option>)}
            </select>
            <span style={S.hint}>{t("at")}</span>
            <select style={S.sel} value={settings.schedule_email_hour} disabled={!isAdmin}
              onChange={e => save({ schedule_email_hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) =>
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
            </select>
            <select style={S.sel} value={settings.schedule_email_target} disabled={!isAdmin}
              onChange={e => save({ schedule_email_target: e.target.value })}>
              <option value="next">{t("next week's schedule")}</option>
              <option value="current">{t("this week's schedule")}</option>
            </select>
          </>
        )}
      </div>
      {!on && <p style={S.hint}>⏸ {t("Automatic sending is off — you can still send manually.")}</p>}

      {/* Άμεση αποστολή */}
      <div style={{ ...S.row, borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>
        <button style={{ ...S.btn, opacity: busy ? 0.6 : 1 }} disabled={busy || !isAdmin} onClick={sendNow}>
          📤 {busy ? t("Sending…") : t("Send now")}
        </button>
        {preview && (
          <span style={S.hint}>
            {t("Will reach")} <b>{preview.recipients}</b> {t("people")} · {preview.shifts} {t("shifts")}
            {preview.skipped > 0 && <> · <span style={{ color: "#dc2626" }}>{preview.skipped} {t("without email")}</span></>}
            {preview.weekLabel && <> · {preview.weekLabel}</>}
          </span>
        )}
      </div>
      {flash && (
        <div style={{ marginTop: 8, padding: "9px 12px", borderRadius: 10, fontSize: 14,
          background: flash.type === "ok" ? "#ecfdf3" : "#fee2e2",
          color: flash.type === "ok" ? "#065f46" : "#991b1b" }}>{flash.text}</div>
      )}

      {/* Ιστορικό */}
      {log.length > 0 && (
        <div style={{ marginTop: 14, borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{t("Last sent")}</div>
          {log.slice(0, 5).map(r => (
            <div key={r.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid #fafafa" }}>
              <span>{new Date(r.created_at).toLocaleString(locale,
                { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              {" · "}
              <span style={S.chip}>{r.trigger_type === "auto" ? t("automatic") : t("manual")}</span>
              {" "}
              <b>{r.recipients}</b> {t("people")} · {t("week")} {r.week}
              {(r.sent_by_name || r.sent_by_username) &&
                <span style={S.hint}> · {r.sent_by_name || r.sent_by_username}</span>}
              {" "}
              <button style={{ ...S.ghost, padding: "2px 8px", fontSize: 12 }}
                onClick={() => setOpenLog(openLog === r.id ? null : r.id)}>
                {openLog === r.id ? t("hide") : t("what was sent")}
              </button>
              {openLog === r.id && (
                <div style={{ margin: "8px 0 8px 12px", padding: "8px 12px", background: "#fafafa",
                              borderRadius: 8, borderLeft: "3px solid #e5e7eb" }}>
                  {(r.detail || []).map((d, i) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <b>{d.name}</b>{" "}
                      {d.skipped
                        ? <span style={{ color: "#dc2626" }}>({t("no email")})</span>
                        : <span style={S.hint}>{d.email}</span>}
                      <div style={{ ...S.hint, marginLeft: 10 }}>
                        {(d.shifts || []).map((s, j) => (
                          <div key={j}>
                            {new Date(s.date).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" })}
                            {" "}{s.starts}–{s.ends} · {s.role}{s.area ? ` · ${s.area}` : ""}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {log.length === 0 && <p style={{ ...S.hint, marginTop: 10 }}>{t("No schedule has been emailed yet.")}</p>}
    </div>
  );
}
